(ns rothko.editor.behaviours.insert
  (:require [com.rpl.specter :as sp]
            [rothko.editor.document.navigators :as n]
            [rothko.editor.edit.paths :as edit-paths]
            [rothko.editor.mark.transform :as mark-transform]
            [rothko.editor.matcher :as m]
            [rothko.editor.space.transform :as space-transform]
            [rothko.editor.util :as util :include-macros true]))

(declare next-transform)

(def transform
  (sp/recursive-path
   [{:keys [name keep-system-flag? space-edits?] :as opts}]
   insert
   [(util/cond-path

     ;; Empty document
     n/empty-document?
     (edit-paths/create name)

     ;; Space token at the end of the script
     (n/context? [m/?any [{:type :space}] n/script-end-matcher])
     (sp/multi-path
      n/move-selection-one-char-to-right
      (edit-paths/create name))

     ;; Space token at the start of the script
     (n/context? [n/script-start-matcher [{:type :space}] m/?any])
     (edit-paths/create name)

     ;; At the end of a space token
     (n/and-paths
      (n/context? [m/?any [{:type :space}] m/?any])
      n/end-of-token?)
     (sp/multi-path
      ;; Move the selection one char to the right
      n/move-selection-one-char-to-right
      ;; then re-run this transformation
      insert)

     ;; Caret at the space-edit token
     (n/and-paths
      (n/context? [m/?any [{:type :space-edit}] m/?any])
      n/CARET?)
     (sp/multi-path
      (sp/if-path
       n/end-of-token?
       n/move-selection-one-char-to-right)
      (edit-paths/create name)
      (n/merge-similar keep-system-flag?))

     ;; Within a space token
     (n/and-paths
      (n/context? [m/?any [{:type :space}] m/?any])
      (n/char-context? [#"\s\s?" m/?any #"\s\s?"]))
     (sp/if-path
      (n/or-paths n/CARET? (constantly (not space-edits?)))
      (space-transform/split-space-and-insert-edit name)
      [(edit-paths/create-from-selection name opts)])

     ;; When selecting a space, mark it as edited and replace it in a new token
     (n/context? [m/?any [{:type :space}] m/?any])
     [space-transform/remove-dangling-spaces
      (space-transform/edit-selection-and-add-new-chars name)]

     ;; When selecting an edit (and optionally spaces), insert the replacement in place
     (let [removable-space (space-transform/removable-space space-edits?)]
       (n/context? [m/?any (m/every (m/one-of {:type :edit} {:type removable-space})) m/?any]))
     [space-transform/remove-dangling-spaces
      (sp/multi-path
       (n/selection-insert name keep-system-flag? :edit)
       (n/merge-similar keep-system-flag?))]

     ;; Split transform due to 'more than 20 params' error when running tests in JVM caused by over 20 references to parameters.
     (constantly :else)
     (next-transform opts))]))

(defn next-transform
  [{:keys [name punctuation? insert-before-word? space-before-replacement? allow-overwriting-annotation? remove-dangling-spaces?] :as opts}]
  (util/cond-path

   ;; Append punctuation to a word
   (n/and-paths
    (constantly punctuation?)
    (n/context? [m/?any [{:type :word}] m/?any])
    (n/or-paths n/start-of-token? n/end-of-token?))
   (sp/multi-path
    (sp/if-path
     n/end-of-token?
     n/move-selection-one-char-to-right)
    (edit-paths/create name))

   ;; Insert an edit with caret immediately in front of a word
   (n/and-paths
    (constantly insert-before-word?)
    n/start-of-token?
    (n/context? [m/?any [{:type :word}] m/?any]))
   (edit-paths/create name)

   ;; Within unedited word and space tokens
   (n/context?
    [m/?any
     (m/and-matcher
      (m/every (m/one-of (m/not-matcher {:edited? true})))
      (m/every (m/one-of {:type :word} {:type :space})))
     m/?any])
   [(when remove-dangling-spaces?
      space-transform/remove-dangling-spaces)
    (sp/multi-path
     (edit-paths/create-from-selection name opts)
     mark-transform/remove-system?-flags)]

   ;; Selection intersecting system tag token
   (n/context?
    [m/?any
     (m/and-matcher
      (m/every (m/one-of (m/not-matcher {:edited? true})))
      (m/every (m/one-of {:type :tag :system? true} ; Allows the editing of only system tags in stage 2 and ..
                         {:type :tag :previous-stage? true} ; .. all tags added in the previous stage when in stage 3 (including system and non-system tags)
                         {:type :word}
                         {:type :space})))
     m/?any])
   (sp/multi-path
    (n/expand-selection
     {:new-start-offset 0
      :new-end-offset :last})
    [space-transform/remove-dangling-spaces
     (edit-paths/create-from-selection name opts)])

   ;; At the end of an edited word token
   (n/and-paths
    (constantly (not space-before-replacement?))
    (n/context? [m/?any [{:type :word :edited? true}] m/?any])
    n/end-of-token?)
   (sp/multi-path
    n/move-selection-one-char-to-right
    (edit-paths/create name)
    (n/merge-similar))

   ;; Span containing edited words and/or (space-)edits in addition to optional words and spaces
   (n/and-paths
    (constantly allow-overwriting-annotation?)
    (n/context?
     [m/?any
      (m/every (m/one-of {:type :word} {:type :edit} {:type :space} {:type :space-edit}))
      (m/not-matcher (m/starts-with [{:type :edit}]))]))
   (edit-paths/create-from-selection name (assoc opts :remove-edits? true))))
